import React from 'react';
import styled from 'styled-components';

import {
  CustomFieldWithValue,
  errorFieldsTypes,
  ErrorsFields,
  MultiOptionValue,
  prefixTypes,
  RequiredFields,
} from '@/core/interfaces/common';
import { FormInput } from '@/core/components/FormInput';
import { FormSelect } from '@/core/components/FormSelect';
import { CustomFormInput } from '@/core/components/CustomFormInput';
import { AutoAddressInput } from '@/core/components/AutoAddressInput';

import { prefixes } from '@/utils/constants';
import mediaQueries from '@/utils/mediaQueries';

const {
  small,
} = mediaQueries;

const CustomContactFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 32px;

  // Somewhat arbitrarily chosen
  @media (min-width: 624px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px 24px;
  }

  // Breakpoint for new form going UNDER the description
  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 16px 24px;
  }

  // This is the breakpoint
  @media (min-width: 1440px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export interface CustomFieldErrors {
  value: Array<string>;
}

export interface ContactFieldsProps {
  backgroundColor: string;
  error: ErrorsFields;
  email: string;
  firstName: string;
  lastName: string;
  streetAddress: string;
  phoneNumber: string | number;
  prefix: prefixTypes;
  zipCode: string;
  customFields: Array<CustomFieldWithValue>;
  handleBlurAddress?: (streetAddress?: string, zipCode?: string) => void;
  handleChangeAddress: (value: string) => void;
  handleClearCustomFieldError: (value: number) => void;
  handleClearFieldError: (value: string) => void;
  handleCustomFieldChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    index: number
  ) => void;
  handleMultiCustomFieldChange: (
    values: Array<MultiOptionValue>,
    index: number,
  ) => void;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handlePhoneInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handleSetZipCode: (value: string) => void;
  renderContactError: (value: errorFieldsTypes) => string;
  requiredFields?: RequiredFields;
}

export const FormInputWrapper = styled.div`
  padding-top: 0;
`;

export const CustomContactFields = ({
  backgroundColor,
  error,
  email,
  handleBlurAddress,
  handleChangeAddress,
  handleCustomFieldChange,
  handleClearCustomFieldError,
  handleClearFieldError,
  handleMultiCustomFieldChange,
  handleInputChange,
  handlePhoneInputChange,
  handleSelectChange,
  handleSetZipCode,
  prefix,
  phoneNumber,
  requiredFields,
  firstName,
  lastName,
  renderContactError,
  streetAddress,
  customFields,
  zipCode,
}: ContactFieldsProps) => (
  <CustomContactFieldsWrapper aria-label="Contact fields">
    <FormInputWrapper className="muster-field-prefix">
      <FormSelect
        backgroundcolor={backgroundColor}
        error={() => renderContactError('prefix')}
        name="Prefix"
        onChange={handleSelectChange}
        onFocus={() => handleClearFieldError('prefix')}
        options={prefixes}
        isRequired={!!requiredFields?.prefix}
        value={prefix}
      />
    </FormInputWrapper>
    <FormInputWrapper className="muster-field-firstname">
      <FormInput
        backgroundcolor={backgroundColor}
        error={() => renderContactError('firstName')}
        label="First Name"
        name="firstName"
        onChange={handleInputChange}
        onFocus={() => handleClearFieldError('firstName')}
        isRequired={!!requiredFields?.firstName}
        value={firstName}
      />
    </FormInputWrapper>
    <FormInputWrapper className="muster-field-lastname">
      <FormInput
        backgroundcolor={backgroundColor}
        error={() => renderContactError('lastName')}
        isRequired={!!requiredFields?.lastName}
        label="Last Name"
        name="lastName"
        onChange={handleInputChange}
        onFocus={() => handleClearFieldError('lastName')}
        value={lastName}
      />
    </FormInputWrapper>
    <FormInputWrapper className="muster-field-address">
      <AutoAddressInput
        backgroundcolor={backgroundColor}
        error={() => renderContactError('streetAddress')}
        isRequired={!!requiredFields?.streetAddress}
        onBlur={handleBlurAddress}
        onChangeAddress={handleChangeAddress}
        onFocus={() => handleClearFieldError('streetAddress')}
        setZipCode={handleSetZipCode}
        value={streetAddress}
      />
    </FormInputWrapper>
    <FormInputWrapper className="muster-field-zipcode">
      <FormInput
        backgroundcolor={backgroundColor}
        error={() => renderContactError('zipCode')}
        isRequired={!!requiredFields?.zipCode}
        label="Zip Code"
        maxlength={5}
        name="zipCode"
        onBlur={handleBlurAddress}
        onChange={handleInputChange}
        onFocus={() => handleClearFieldError('zipCode')}
        type="text"
        value={zipCode}
      />
    </FormInputWrapper>
    <FormInputWrapper className="muster-field-email">
      <FormInput
        backgroundcolor={backgroundColor}
        error={() => renderContactError('email')}
        isRequired={!!requiredFields?.email}
        label="Email"
        name="email"
        onChange={handleInputChange}
        onFocus={() => handleClearFieldError('email')}
        type="email"
        value={email}
      />
    </FormInputWrapper>
    <FormInputWrapper className="muster-field-phone">
      <FormInput
        backgroundcolor={backgroundColor}
        error={() => renderContactError('phoneNumber')}
        isRequired={!!requiredFields?.phoneNumber}
        label="Phone number"
        maxlength={14}
        name="phoneNumber"
        onChange={handlePhoneInputChange}
        onFocus={() => handleClearFieldError('phoneNumber')}
        type="text"
        value={phoneNumber}
      />
    </FormInputWrapper>
    {customFields?.map((customFieldData, index) => (
      <FormInputWrapper
        className="muster-field-custom"
        key={customFieldData.uuid}
      >
        <CustomFormInput
          backgroundcolor={backgroundColor}
          customFieldData={customFieldData}
          error={() => !!error?.customFields &&
            !!error?.customFields.length &&
            !!error?.customFields[index].value &&
            error?.customFields[index].value[0]
          }
          isRequired={!!requiredFields?.custom}
          onChange={event => handleCustomFieldChange(event, index)}
          onMultiChange={values => handleMultiCustomFieldChange(values, index)}
          onFocus={() => handleClearCustomFieldError(index)}
        />
      </FormInputWrapper>
    ))}
  </CustomContactFieldsWrapper>
);

CustomContactFields.defaultProps = {
  handleBlurAddress: () => null,
};
